import {useContext, useEffect, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import SectionHeader from "../../../../util/component/SectionHeader";
import AdvancedFilter from "../../component/AdvancedFilter";
import * as Yup from 'yup';
import {Button, Checkbox, Chip, Grid, ListItemText, MenuItem, Stack, TextField, useTheme} from "@mui/material";
import {useRecoilState} from "recoil";
import {SEGMENT_LIST} from "../../service/SalesMarketingRecoil";
import {DatePicker} from "@mui/x-date-pickers";
import {AgGridReact} from "ag-grid-react";
import {
    LEISURE_AGREEMENT_STATUS_DISMISSED,
    LEISURE_AGREEMENT_STATUS_DRAFT,
    LEISURE_AGREEMENT_STATUS_READY
} from "../../component/Constants";
import {useStatusValuesLeisureAgreement} from "../../component/useStatusValuesLeisureAgreement";
import AgGridCustomTable from "../../../../util/component/AgGridCustomTable";
import {ColDef, GetRowIdParams, GridApi, GridOptions, IServerSideGetRowsParams} from "ag-grid-enterprise";
import {LibraryAddRounded} from "@mui/icons-material";
import {useNavigate} from "react-router-dom";
import {useProductCodeActions} from "../../service/useProductCodeActions";
import {useLeisureAgreementActions} from "../../service/useLeisureAgreementActions";
import LeisureAgreementDetailModal from "./modals/LeisureAgreementDetailModal";
import dayjs from "dayjs";
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";
import EmptyState from "../../../../util/component/EmptyState";
import ActionButtonWithTooltip, {ActionTypes} from "../../component/ActionButtonWithTooltip";
import {useHasScope} from "../../../../config/globalConfig";

const LeisureAgreementListPage = () => {

    const {globalData} = useContext(GlobalContext);
    const {t} = useTranslation(['salesmarketing']);
    const productCodeActions = useProductCodeActions();
    const leisureAgreementActions = useLeisureAgreementActions();
    const navigate = useNavigate();
    const [searchInput, setSearchInput] = useState("");
    const [segments] = useRecoilState(SEGMENT_LIST);
    const {getStatusColorAgreement, getStatusLabelAgreement} = useStatusValuesLeisureAgreement();
    const gridRef = useRef<AgGridReact>(null);
    const [openDetail, setOpenDetail] = useState(false);
    const [selectedLeisureAgreement, setSelectedLeisureAgreement] = useState<any>(null);
    const theme = useTheme();
    const accentColor = theme.palette.accent.main;
    const hasScopes = useHasScope;

    const filterDefaultValues = {
        legalEntity: globalData?.user?.legalEntities?.map((le: any) => le.name),
        dealer: globalData?.user?.dealers?.map((d: any) => d.name),
        segment: "",
        status: [],
        name: "",
        code: "",
        description: "",
        startDate: null,
        endDate: null
    };
    const [filters, setFilters] = useState<any>(filterDefaultValues);

    const validationObj = {
        status: Yup.array(),
        segment: Yup.string().nullable(),
        startDate: Yup.date().nullable()
            .test("testAgreementStartDate", t('salesmarketing:leisureAgreement.filters.dateRangeCheck'), (value, context) => {
                return (!value || !context.parent.endDate || value <= context.parent.endDate)
            })
            .typeError(t('salesmarketing:leisureAgreement.filters.invalidDate')),
        endDate: Yup.date().nullable()
            .test("testAgreementEndDate", t('salesmarketing:leisureAgreement.filters.dateRangeCheck'), (value, context) => {
                return (!value || !context.parent.startDate || value >= context.parent.startDate)
            })
            .typeError(t('salesmarketing:leisureAgreement.filters.invalidDate')),
        name: Yup.string().nullable(),
        description: Yup.string().nullable(),
    }

    const statuses = [
        {
            value: LEISURE_AGREEMENT_STATUS_DRAFT,
            label: getStatusLabelAgreement(LEISURE_AGREEMENT_STATUS_DRAFT),
            color: getStatusColorAgreement(LEISURE_AGREEMENT_STATUS_DRAFT)
        },
        {
            value: LEISURE_AGREEMENT_STATUS_READY,
            label: getStatusLabelAgreement(LEISURE_AGREEMENT_STATUS_READY),
            color: getStatusColorAgreement(LEISURE_AGREEMENT_STATUS_READY)
        },
        {
            value: LEISURE_AGREEMENT_STATUS_DISMISSED,
            label: getStatusLabelAgreement(LEISURE_AGREEMENT_STATUS_DISMISSED),
            color: getStatusColorAgreement(LEISURE_AGREEMENT_STATUS_DISMISSED)
        },

    ];

    const fields = {
        segment: (field: any) => (
            <TextField
                fullWidth
                label={t('salesmarketing:leisureAgreement.filters.segmentField')}
                select
                value={field.value}
                onChange={field.onChange}
            >
                <MenuItem key={""} value={""}/>
                {segments?.map((s: any) => (
                    <MenuItem key={s.segmentCode} value={s.segmentCode}>
                        {s.segmentName}
                    </MenuItem>
                ))}
            </TextField>
        ),
        status: (field: any) => (
            <TextField
                fullWidth
                label={t('salesmarketing:leisureAgreement.filters.statusField')}
                select
                SelectProps={{
                    multiple: true,
                    renderValue: (selected: any) => selected.map((s: any) => statuses.find(ss => ss.value === s)!.label).join(', ')
                }}
                value={field.value}
                onChange={field.onChange}
            >
                {statuses?.map((status) => (
                    <MenuItem key={status.value} value={status.value}>
                        <Checkbox checked={field.value.indexOf(status.value) > -1}/>
                        <span style={{
                            border: "1px solid " + status.color,
                            backgroundColor: status.color,
                            borderRadius: "50%",
                            display: "inline-block",
                            height: "14px",
                            width: "14px",
                            marginRight: "5px"
                        }}/>
                        <ListItemText primary={status.label}/>
                    </MenuItem>
                ))}
            </TextField>
        ),
        name: (field: any) => (
            <TextField
                fullWidth
                label={t('salesmarketing:leisureAgreement.filters.agreementNameField')}
                value={field.value}
                onChange={field.onChange}
            />
        ),
        code: (field: any) => (
            <TextField
                fullWidth
                label={t('salesmarketing:leisureAgreement.filters.agreementCodeField')}
                value={field.value}
                onChange={field.onChange}
            />
        ),
        description: (field: any) => (
            <TextField
                fullWidth
                label={t('salesmarketing:leisureAgreement.filters.agreementDescriptionField')}
                value={field.value}
                onChange={field.onChange}
            />
        ),
        startDate: (field: any, error: any, values: any) => (
            <DatePicker
                views={['day']}
                label={t('salesmarketing:leisureAgreement.filters.agreementStartDateField')}
                value={field.value}
                onChange={field.onChange}
                maxDate={values["endDate"] ? values["endDate"] : ""}
                renderInput={(params) =>
                    <TextField {...params} fullWidth
                               id={"agreementStartDate"}
                               error={error !== undefined}
                               helperText={error ? error.message : ""}
                    />}
                inputFormat={"L"}
            />
        ),
        endDate: (field: any, error: any, values: any) => (
            <DatePicker
                views={['day']}
                label={t('salesmarketing:leisureAgreement.filters.agreementEndDateField')}
                value={field.value}
                onChange={field.onChange}
                minDate={values["startDate"] ? values["startDate"] : ""}
                renderInput={(params) =>
                    <TextField {...params} fullWidth
                               id={"agreementEndDate"}
                               error={error !== undefined}
                               helperText={error ? error.message : ""}
                    />}
                inputFormat={"L"}
            />
        )
    };

    const columns: ColDef[] = [
        {
            field: "tenantId",
            headerName: t('salesmarketing:leisureAgreement.listColumns.legalEntityField'),
            hide: globalData?.user?.legalEntities?.length <= 1,
            unSortIcon: true,
            cellRenderer: (params: any) => {
                return globalData?.user?.legalEntities.find((le: any) => le.name === params.data.tenantId)?.name
            },
            tooltipValueGetter: (params: any) => {
                return globalData?.user?.legalEntities.find((le: any) => le.name === params.data.tenantId)?.name
            }
        },
        {
            field: "dealerId",
            headerName: t('salesmarketing:leisureAgreement.listColumns.dealerField'),
            hide: globalData?.user?.dealers?.length <= 1,
            unSortIcon: true,
            cellRenderer: (params: any) => {
                return globalData?.user?.dealers.find((le: any) => le.name === params.data.dealerId)?.name
            },
            tooltipValueGetter: (params: any) => {
                return globalData?.user?.dealers.find((le: any) => le.name === params.data.dealerId)?.name
            }
        },
        {
            field: "customerName",
            headerName: t('salesmarketing:leisureAgreement.listColumns.customerNameHeader'),
            unSortIcon: true
        },
        {
            field: "customerId",
            headerName: t('salesmarketing:leisureAgreement.listColumns.customerIdHeader'),
            unSortIcon: true
        },
        {
            field: "name",
            headerName: t('salesmarketing:leisureAgreement.listColumns.nameHeader'),
            unSortIcon: true
        },
        {
            field: "description",
            headerName: t('salesmarketing:leisureAgreement.listColumns.descriptionHeader'),
            unSortIcon: true,
            valueFormatter: (params: any) => params.value ? params.value : '--'
        },
        {
            field: "leisureCode",
            headerName: t('salesmarketing:leisureAgreement.listColumns.leisureCodeHeader'),
            unSortIcon: true
        },
        {
            field: "startDate",
            headerName: t('salesmarketing:leisureAgreement.listColumns.startDateHeader'),
            cellRenderer: (params: any) => params.value ? dayjs(params.value).format("L") : "",
            unSortIcon: true
        },
        {
            field: "endDate",
            headerName: t('salesmarketing:leisureAgreement.listColumns.endDateHeader'),
            cellRenderer: (params: any) => params.value ? dayjs(params.value).format("L") : "",
            unSortIcon: true
        },
        {
            field: "status",
            maxWidth: 160,
            headerName: t('salesmarketing:leisureAgreement.listColumns.statusHeader'),
            pinned: 'right',
            lockPosition: 'right',
            lockPinned: true,
            cellClass: "grid-cell-center",
            headerClass: "grid-header-center",
            unSortIcon: true,
            cellRenderer: (params: any) => {
                return <Chip label={getStatusLabelAgreement(params.data.status)}
                             sx={{backgroundColor: getStatusColorAgreement(params.data.status)}}/>
            }
        },
        {
            field: "_actions",
            maxWidth: 160,
            headerName: t('salesmarketing:leisureAgreement.listColumns.actionHeader'),
            pinned: 'right',
            lockPosition: 'right',
            lockPinned: true,
            sortable: false,
            cellRenderer: (params: any) => {
                return (
                    <Stack direction={"row"} justifyContent={"center"}>
                        <ActionButtonWithTooltip type={ActionTypes.edit} callback={() => {
                            //navigate(`/salesmarketing/leisureagreementupdate/${params.data.id}`, {state: {promo: params.data}})
                            window.open(`/salesmarketing/leisureagreementupdate/${params.data.id}`, "_blank")
                        }} auth={'SCOPE_SM_UPDATE_LEISURE_AGREEMENT'}/>
                        <ActionButtonWithTooltip type={ActionTypes.details} callback={() => {
                            setOpenDetail(true);
                            setSelectedLeisureAgreement(params.data);
                        }} auth={'SCOPE_SM_VIEW_LEISURE_AGREEMENT_DETAIL'}/>
                    </Stack>
                );
            }
        }
    ];

    useEffect(() => {
        gridRef?.current?.api?.setServerSideDatasource(createDatasource(filters, gridRef.current!.api));
    }, [filters, segments]);

    function createDatasource(filters: any, api: GridApi) {
        return {
            getRows: async (params: IServerSideGetRowsParams) => {
                await productCodeActions.listSegment();
                let filterTemp = Object.assign({}, filters);
                filterTemp.offset = params.request.startRow ? params.request.startRow : 0;
                filterTemp.limit = (params.request.endRow ? params.request.endRow : 10) - filterTemp.offset;
                filterTemp.sort = params.request.sortModel.length === 0 ? null : params.request.sortModel[0].colId;
                filterTemp.order = params.request.sortModel.length === 0 ? null : params.request.sortModel[0].sort;
                filterTemp.search = searchInput;
                let data = await leisureAgreementActions.listAgreement(filterTemp);
                params.success({
                    rowData: data?.content,
                    rowCount: data?.totalElements
                });
                if (data.totalElements === 0) {
                    api.showNoRowsOverlay();
                } else {
                    api.hideOverlay();
                }
            }
        };
    };

    const gridOptions: GridOptions = {
        getRowId: (params: GetRowIdParams) => params.data['id'],
        suppressRowClickSelection: true,
        domLayout: 'autoHeight',
        rowModelType: 'serverSide',
        defaultColDef: {
            sortable: true,
            resizable: true,
            minWidth: 130
        },
        columnDefs: columns,
        noRowsOverlayComponentParams: {title: t('salesmarketing:leisureAgreement.noRows')}
    }

    const noRowsOverlayComponent = useMemo<any>(() => {
        return EmptyState;
    }, []);

    const toolbar = {
        renderActions: () =>
            hasScopes('SCOPE_SM_CREATE_LEISURE_AGREEMENT') ?
                <Button fullWidth size="small"onClick={() => {
                    navigate('/salesmarketing/leisureagreementcreate')
                }}>
                    <LibraryAddRounded style={{paddingRight: "10px"}}/>
                    {t('salesmarketing:leisureAgreement.buttons.newLeisureAgreement')}
                </Button> :
                <></>
    }

    return (
        <>
            <SectionHeader title={t('salesmarketing:leisureAgreement.listHeader.title')}
                           subtitle={t('salesmarketing:leisureAgreement.listHeader.subTitle')}/>
            <AdvancedFilter validationObj={validationObj} fields={fields} defaultValues={filterDefaultValues}
                            searchInput={searchInput} setSearchInput={setSearchInput} filters={filters}
                            setFilters={setFilters}/>
            <Grid container>
                <Grid item xs={12} sx={{padding: "16px"}}>
                    <AgGridCustomTable
                        onGridReady={(params) => {
                            params.api.setServerSideDatasource(createDatasource(filters, gridRef.current!.api));
                        }}
                        noRowsOverlayComponent={noRowsOverlayComponent} gridOptions={gridOptions}
                        gridRef={gridRef} toolbar={toolbar}/>
                </Grid>
            </Grid>
            {selectedLeisureAgreement &&
                <LeisureAgreementDetailModal key={selectedLeisureAgreement.id} open={openDetail}
                                             handleClose={() => setOpenDetail(false)}
                                             leisureAgreement={selectedLeisureAgreement}/>}
        </>
    );
}

export default LeisureAgreementListPage;